import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import BackButton from "app/components/BackButton";
import { useGetCompany } from "app/hooks/company/useGetCompany";
import useGetJobs from "app/hooks/job/useGetJobs";
import { buildJobDetailPath } from "app/utils/jobSearchSeo";
import { convertISODateTimeToDate, convertToBase64 } from "app/utils/utils";
import { useNavigate, useParams } from "react-router-dom";

// --- MUI Theme from Tailwind config ---
const velocityTheme = createTheme({
  palette: {
    primary: {
      main: "#00103e",
      contrastText: "#ffffff"
    },
    secondary: {
      main: "#006496",
      contrastText: "#ffffff"
    },
    error: {
      main: "#ba1a1a"
    },
    background: {
      default: "#faf8ff",
      paper: "#ffffff"
    },
    text: {
      primary: "#1b1b20",
      secondary: "#444650"
    },
    // Custom tokens via CSS variables workaround
    custom: {
      primaryContainer: "#0a2463",
      secondaryFixed: "#cce5ff",
      onSecondaryFixed: "#001e31",
      tertiaryFixed: "#e8ddff",
      onTertiaryFixed: "#22005d",
      surfaceContainerLowest: "#ffffff",
      surfaceContainerLow: "#f5f3f9",
      surfaceContainerHigh: "#e9e7ed",
      surfaceContainer: "#efedf3",
      outlineVariant: "#c5c6d2",
      onSurfaceVariant: "#444650"
    }
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    h1: { fontFamily: '"Manrope", sans-serif', fontWeight: 800 },
    h2: { fontFamily: '"Manrope", sans-serif', fontWeight: 800 },
    h3: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 },
    h5: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 },
    h6: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 12
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 800,
          letterSpacing: 0,
          fontSize: "0.625rem",
          textTransform: "uppercase",
          borderRadius: 9999
        }
      }
    }
  }
});

// --- Sub-components ---

function StatCard({ icon, label, value }) {
  return (
    <Card
      sx={{
        bgcolor: "background.paper",
        borderBottom: "2px solid transparent",
        transition: "border-color 0.2s",
        "&:hover": { borderBottomColor: "secondary.main" },
        height: "100%"
      }}
    >
      <CardContent sx={{ p: 4, display: "flex", flexDirection: "column", height: "100%" }}>
        <Typography
          component="span"
          className="material-symbols-outlined"
          sx={{ fontSize: 36, color: "#75c3ff", display: "block", mb: 1.5 }}
        >
          {icon}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontFamily: '"Inter", sans-serif',
            fontWeight: 700,
            letterSpacing: 0,
            textTransform: "uppercase",
            color: "text.secondary",
            mb: 0.5
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Manrope", sans-serif',
            fontWeight: 800,
            color: "primary.main",
            fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
            lineHeight: 1.3,
            wordBreak: "break-word",
            whiteSpace: "normal",
            mt: "auto"
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ValueItem({ icon, title, description }) {
  return (
    <Stack direction="row" spacing={3} alignItems="flex-start">
      <Box
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          bgcolor: "#e8ddff",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography
          component="span"
          className="material-symbols-outlined"
          sx={{ color: "#22005d", fontSize: 22 }}
        >
          {icon}
        </Typography>
      </Box>
      <Box>
        <Typography variant="body1" fontWeight={700} mb={0.5}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

function JobCard({ id, title, location, salary, type }) {
  const navigate = useNavigate();
  return (
    <Card
      sx={{
        bgcolor: "background.paper",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
        "& .job-title": { transition: "color 0.2s" },
        "&:hover .job-title": { color: "secondary.main" }
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
        >
          <Box>
            <Typography
              variant="h5"
              className="job-title"
              sx={{
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 800,
                color: "primary.main",
                fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.375rem" },
                lineHeight: 1.3,
                wordBreak: "break-word",
                mb: 1.5
              }}
            >
              {title}
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={2} alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  component="span"
                  className="material-symbols-outlined"
                  sx={{ fontSize: 16, color: "text.secondary" }}
                >
                  location_on
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {location}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  component="span"
                  className="material-symbols-outlined"
                  sx={{ fontSize: 16, color: "text.secondary" }}
                >
                  payments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {salary}
                </Typography>
              </Stack>
              <Chip label={type} size="small" sx={{ bgcolor: "#cce5ff", color: "#001e31" }} />
            </Stack>
          </Box>
          <Button
            variant="contained"
            sx={{
              bgcolor: "#e9e7ed",
              color: "primary.main",
              px: 3,
              py: 1.25,
              flexShrink: 0,
              "&:hover": { bgcolor: "primary.main", color: "#ffffff" }
            }}
            onClick={() => navigate(buildJobDetailPath(title, id))}
          >
            View Details
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SocialLink({ label, icon }) {
  return (
    <Link
      href="#"
      underline="none"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        bgcolor: "background.paper",
        borderRadius: 2,
        transition: "background-color 0.2s",
        "&:hover": { bgcolor: "#ffffff", "& .link-label": { color: "primary.main" } }
      }}
    >
      <Typography className="link-label" variant="body2" fontWeight={500} color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="span"
        className="material-symbols-outlined"
        sx={{ fontSize: 18, color: "text.secondary" }}
      >
        {icon}
      </Typography>
    </Link>
  );
}

// --- Main Page ---

export default function CompanyDetail() {
  const { id } = useParams();
  const { data: company } = useGetCompany({
    id: id
  });

  const { data: companyJobs } = useGetJobs({
    company: id,
    limit: 5,
    offset: 0
  });

  const companyName = company?.jp_company_name || "Company";
  const about = company?.jp_about_company || "No description available.";
  const logo = company?.jp_logo;
  const isVerified = company?.jp_is_verified === 1;
  const jobCount = companyJobs?.count || 0;
  const founded = company?.jp_founded ? convertISODateTimeToDate(company.jp_founded) : "N/A";
  const email = company?.jp_email || "N/A";
  const website = company?.jp_website || "N/A";
  const industry = company?.jp_industry || "N/A";
  const jobs = companyJobs?.data || [];

  return (
    <ThemeProvider theme={velocityTheme}>
      <CssBaseline />

      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&family=Inter:wght@400;500;700&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200"
        rel="stylesheet"
      />

      <Box component="main" sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        {/* ── Hero ── */}
        <Box
          component="section"
          sx={{
            py: { xs: 10, md: 10 },
            background:
              "radial-gradient(circle at 10% 20%, rgba(10,36,99,0.03) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(49,11,119,0.03) 0%, transparent 40%)"
          }}
        >
          <Container maxWidth="lg">
            <Box mb={2}>
              <BackButton label="Go Back" />
            </Box>
            <Stack direction={{ xs: "column", md: "row" }} alignItems="center" spacing={6}>
              {/* Logo */}
              <Box
                sx={{
                  width: { xs: 128, md: 192 },
                  height: { xs: 128, md: 192 },
                  flexShrink: 0,
                  bgcolor: "background.paper",
                  borderRadius: 3,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(197,198,210,0.3)"
                }}
              >
                {logo ? (
                  <img
                    src={`data:image/jpeg;base64,${convertToBase64(logo.data)}`}
                    alt={companyName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: 12
                    }}
                  />
                ) : (
                  <Typography
                    variant="h2"
                    sx={{
                      color: "primary.main",
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 800,
                      fontSize: { xs: "2rem", md: "3rem" },
                      letterSpacing: 0
                    }}
                  >
                    {companyName
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </Typography>
                )}
              </Box>

              {/* Info */}
              <Box flex={1} textAlign={{ xs: "center", md: "left" }}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  spacing={2}
                  alignItems="center"
                  justifyContent={{ xs: "center", md: "flex-start" }}
                  mb={2}
                >
                  <Chip label={industry} sx={{ bgcolor: "#cce5ff", color: "#001e31" }} />
                  {isVerified && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        component="span"
                        className="material-symbols-outlined"
                        sx={{ fontSize: 16, color: "success.main" }}
                      >
                        verified
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={500}>
                        Verified Employer
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                <Typography
                  variant="h1"
                  sx={{
                    color: "primary.main",
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3.25rem" },
                    letterSpacing: 0,
                    lineHeight: 1.15,
                    overflowWrap: "anywhere",
                    mb: 3
                  }}
                >
                  {companyName}
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={3}
                  alignItems="center"
                  justifyContent={{ xs: "center", md: "flex-start" }}
                >
                  {jobCount > 0 ? (
                    <Link
                      href="#openings"
                      underline="none"
                      sx={{
                        color: "secondary.main",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5
                      }}
                    >
                      View {jobCount} active openings
                      <Typography
                        component="span"
                        className="material-symbols-outlined"
                        sx={{ fontSize: 18 }}
                      >
                        arrow_downward
                      </Typography>
                    </Link>
                  ) : (
                    <Typography
                      variant="body2"
                      color="secondary.main"
                      fontWeight={500}
                      fontSize={{ xs: "0.95rem", sm: "1rem" }}
                    >
                      No active openings found at the moment
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* ── Stats Grid ── */}
        <Container maxWidth="lg" sx={{ mt: -6, mb: 12, position: "relative", zIndex: 1 }}>
          <Grid container spacing={3}>
            {[
              { icon: "public", label: "Website", value: website },
              { icon: "calendar_today", label: "Founded", value: founded },
              { icon: "public", label: "Industry", value: industry },
              { icon: "mail", label: "Contact", value: email }
            ].map((s) => (
              <Grid item xs={6} lg={3} key={s.label}>
                <StatCard {...s} />
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ── About + Sidebar ── */}
        <Container maxWidth="lg" sx={{ mb: 16 }}>
          <Grid container spacing={8}>
            {/* Left: About */}
            <Grid item xs={12} md={7}>
              <Typography
                variant="h2"
                sx={{
                  color: "primary.main",
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                  letterSpacing: 0,
                  lineHeight: 1.2,
                  overflowWrap: "anywhere",
                  mb: 3
                }}
              >
                About {companyName}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.0625rem" },
                  lineHeight: 1.7,
                  mb: 5,
                  fontWeight: 300
                }}
              >
                {about}
              </Typography>
            </Grid>

            {/* Right: Image + Social */}
            <Grid item xs={12} md={5}>
              <Stack spacing={3}>
                {/* Social Links */}
                {/* <Card sx={{ bgcolor: "#f5f3f9" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                      <Typography
                        component="span"
                        className="material-symbols-outlined"
                        sx={{ color: "secondary.main", fontSize: 20 }}
                      >
                        share
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                        Connect with Velocity
                      </Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                      <SocialLink label="LinkedIn" icon="open_in_new" />
                      <SocialLink label="Twitter / X" icon="open_in_new" />
                      <SocialLink label="velocity.data" icon="link" />
                    </Stack>
                  </CardContent>
                </Card> */}
              </Stack>
            </Grid>
          </Grid>
        </Container>

        {/* ── Open Positions ── */}
        <Box component="section" id="openings" sx={{ bgcolor: "#f5f3f9", py: 12 }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "flex-end" }}
              spacing={3}
              mb={6}
            >
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    color: "primary.main",
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                    letterSpacing: 0,
                    lineHeight: 1.2,
                    mb: 1
                  }}
                >
                  Open Positions
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={{ xs: "0.95rem", md: "1rem" }}
                  lineHeight={1.6}
                >
                  Explore active job openings at{" "}
                  <Typography component="span" color="primary.main" fontWeight={700}>
                    {companyName}
                  </Typography>{" "}
                  and find your next career opportunity!
                </Typography>
              </Box>
              {/* <Button
                variant="text"
                endIcon={
                  <Typography
                    component="span"
                    className="material-symbols-outlined"
                    sx={{ fontSize: "20px !important" }}
                  >
                    arrow_forward
                  </Typography>
                }
                sx={{
                  color: "secondary.main",
                  fontWeight: 700,
                  borderBottom: "2px solid",
                  borderColor: "secondary.main",
                  borderRadius: 0,
                  pb: 0.5,
                  flexShrink: 0
                }}
              >
                View All Open Positions
              </Button> */}
            </Stack>

            <Stack spacing={3}>
              {jobs.length > 0 ? (
                jobs.map((job) => {
                  const id = job?.jp_id;
                  const jobTitle = job?.jp_title || "N/A";
                  const jobLocation = job?.jp_location || "N/A";
                  const salary =
                    job?.jp_minimum_salary && job?.jp_maximum_salary
                      ? `₹${job.jp_minimum_salary}/year - ₹${job.jp_maximum_salary}/year`
                      : "N/A";
                  const type = job?.jp_type || "N/A";
                  return (
                    <JobCard
                      key={id}
                      id={id}
                      title={jobTitle}
                      location={jobLocation}
                      salary={salary}
                      type={type}
                    />
                  );
                })
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={{ xs: "0.95rem", sm: "1rem" }}
                >
                  No open positions available.
                </Typography>
              )}
            </Stack>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
